import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAuthenticated, persistLoginSession, clearSessionStorage, getAccessToken, getRefreshToken } from '../utils/auth';
import { bootstrapSession } from '../utils/bootstrapSession';
import { getSession, subscribeSession } from '../utils/sessionStore';
import { logout as apiLogout, refreshSessionTokens } from '../utils/api';
import { decodeTokenClaims, isAccessTokenExpired } from '../utils/tokenClaims';

const AuthContext = createContext(null);

/** Small buffer before JWT exp so refresh happens while the access token is still usable. */
const REFRESH_SKEW_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 5 * 1000;
const MAX_REFRESH_FAILURE_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Delay until proactive refresh, derived only from the access JWT `exp` (set by backend).
 * Returns null when exp is missing — no frontend lifetime assumption.
 */
function msUntilProactiveRefresh(accessToken) {
  const claims = decodeTokenClaims(accessToken);
  if (!claims?.exp) return null;
  return Math.max(MIN_REFRESH_DELAY_MS, claims.exp * 1000 - Date.now() - REFRESH_SKEW_MS);
}

export function AuthProvider({ children }) {
  const [session, setSessionState] = useState(() => getSession());
  const [status, setStatus] = useState(() => (isAuthenticated() ? 'loading' : 'anonymous'));

  useEffect(() => {
    return subscribeSession((next) => setSessionState({ ...next }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!isAuthenticated()) {
        if (!cancelled) {
          setStatus('anonymous');
        }
        return;
      }
      setStatus('loading');
      try {
        const access = getAccessToken();
        const refresh = getRefreshToken();
        if (refresh && (!access || isAccessTokenExpired(access))) {
          await refreshSessionTokens();
        }
        await bootstrapSession();
        if (!cancelled) setStatus('authenticated');
      } catch {
        clearSessionStorage();
        if (!cancelled) setStatus('anonymous');
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Refresh using access JWT exp from the backend — no hardcoded access lifetime. */
  useEffect(() => {
    if (status !== 'authenticated') return undefined;

    let timerId;
    let cancelled = false;
    let failureBackoffMs = MIN_REFRESH_DELAY_MS;

    const schedule = (delayOverride) => {
      if (cancelled) return;
      if (!getRefreshToken()) return;

      let delay = delayOverride;
      if (delay == null) {
        const access = getAccessToken();
        delay = access ? msUntilProactiveRefresh(access) : null;
      }
      if (delay == null) return;

      timerId = setTimeout(async () => {
        if (cancelled) return;
        try {
          if (getRefreshToken()) {
            await refreshSessionTokens();
            failureBackoffMs = MIN_REFRESH_DELAY_MS;
          }
          schedule();
        } catch {
          failureBackoffMs = Math.min(failureBackoffMs * 2, MAX_REFRESH_FAILURE_BACKOFF_MS);
          schedule(failureBackoffMs);
        }
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [status]);

  const login = useCallback(async (data, email) => {
    persistLoginSession(data, email);
    await bootstrapSession({
      email,
      role: data.role,
      allowUserAppOnWeb: data.allowUserAppOnWeb,
      allowAccessWithoutPayment: data.allowAccessWithoutPayment,
      canAccessRobots: data.canAccessRobots,
      subscriptionAdmin: data.subscriptionAdmin || null,
    });
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    setStatus('anonymous');
    await apiLogout();
  }, []);

  const refreshSession = useCallback(async () => {
    if (!isAuthenticated()) {
      setStatus('anonymous');
      return null;
    }
    return bootstrapSession();
  }, []);

  const value = useMemo(
    () => ({
      session,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      login,
      logout,
      refreshSession,
    }),
    [session, status, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
